import { useEffect } from 'react'
import { report } from '@/lib/telemetry'

/**
 * Keep the screen awake while scanning.
 *
 * Without this the display dims and locks mid-session — which on iOS also
 * suspends the camera track, so the operator returns to a black preview and
 * assumes the app has crashed. Supported in Safari 16.4+; a no-op elsewhere.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    if (!('wakeLock' in navigator)) return

    let sentinel: WakeLockSentinel | null = null
    let cancelled = false

    const acquire = async (): Promise<void> => {
      try {
        const lock = await navigator.wakeLock.request('screen')
        if (cancelled) {
          void lock.release()
          return
        }
        sentinel = lock
      } catch (err) {
        // Rejected when the page is not visible, or on low battery. Neither is
        // worth surfacing to the user.
        report({
          level: 'info',
          event: 'wakelock.request_failed',
          message: err instanceof Error ? err.message : String(err),
        })
      }
    }

    void acquire()

    // The lock is dropped automatically whenever the page is hidden, and is not
    // restored on return — so it has to be re-requested explicitly.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible' && sentinel === null) void acquire()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      void sentinel?.release().catch(() => undefined)
      sentinel = null
    }
  }, [active])
}
